import { useState, useEffect, useCallback } from 'react';
import { some } from 'lodash';
import common from 'infra/utils/common';
import {
  isActivateAudienceEnabled,
  isDeterministicDynamicActivationAllowed,
  deterministicPermissionName,
  customPricePermissionName,
  optimizeSegmentPermissionName,
} from 'data/audience-segment-builder-helper';

/**
 * Hook for managing audience activation states and handlers
 */
const useAudienceActivation = ({ context, audienceChannel, abiPermissions, user, dspService, segment }) => {
  const audienceAppForChannel = context.current.audience_app[audienceChannel];

  // Activation states
  const [isActivateAudienceEnabledState, setIsActivateAudienceEnabledState] = useState(false);
  const [activateAudienceDisabledText, setActivateAudienceDisabledText] = useState('');
  const [isActivateAudienceVisible, setIsActivateAudienceVisible] = useState(false);
  const [isActivateEnabled, setIsActivateEnabled] = useState(!audienceAppForChannel.audience_targetDisabled);
  const [marketsAndAdvertisersPromise, setMarketsAndAdvertisersPromise] = useState(null);

  // Derived values from context
  const audienceId = audienceAppForChannel.audience_id;
  const audienceName = audienceAppForChannel.audience_name;
  const isAudienceDeterministicActivated = audienceAppForChannel.is_audience_deterministic_activated;
  const isAudienceAmplifiedActivated = audienceAppForChannel.is_audience_amplified_activated;
  const isAudienceAlwaysOnActivated = audienceAppForChannel.is_audience_always_on_activated;
  const activatedAmplifiedThreshold = audienceAppForChannel.activated_amplified_threshold;
  const activatedAlwaysOnThreshold = audienceAppForChannel.activated_always_on_threshold;
  const activatedMarket = audienceAppForChannel.activated_market;
  const activatedAdvertiser = audienceAppForChannel.activated_advertiser;
  const activatedDataContractId = audienceAppForChannel.activated_data_contract_id;
  const activatedDataContractText = audienceAppForChannel.activated_data_contract_text;
  const activatedCategoryId = audienceAppForChannel.activated_category_id;

  // Permission checks
  const isFirstParty = some(segment, { type: '1st party' });
  const hasAmplificationModeSelector = !isFirstParty && common.hasAmplificationModeSelector(context, abiPermissions);
  const isDeterministicDynamicAllowed = isDeterministicDynamicActivationAllowed(segment);
  const allowDeterministicSegment = abiPermissions.hasPermission(deterministicPermissionName);
  const allowCustomPriceSegment = abiPermissions.hasPermission(customPricePermissionName);
  const allowOptimizeSegment = abiPermissions.hasPermission(optimizeSegmentPermissionName);

  /**
   * Update audience activation UI state
   */
  const updateAudienceActivationUi = useCallback(async () => {
    const channelAudience = context.current.audience_app[audienceChannel];
    const tempAudience = {
      id: channelAudience.audience_id,
      name: channelAudience.audience_name,
      segment: channelAudience.audience_segment,
      activation: channelAudience.audience_activation,
    };
    const { isEnabled, isVisible, disabledText } = await isActivateAudienceEnabled(
      abiPermissions,
      audienceChannel,
      tempAudience,
      user.id
    );
    setIsActivateAudienceEnabledState(isEnabled);
    if (isEnabled) {
      const promise = dspService.getAmplificationMarketContext('value');
      setMarketsAndAdvertisersPromise(promise);
    }
    setIsActivateAudienceVisible(isVisible);
    setActivateAudienceDisabledText(disabledText);
    setIsActivateEnabled(!audienceAppForChannel.audience_targetDisabled || isVisible);
  }, [context, audienceChannel, abiPermissions, user, dspService, audienceAppForChannel]);

  // Update activation UI on mount
  useEffect(() => {
    updateAudienceActivationUi();
  }, [updateAudienceActivationUi]);

  /**
   * Update deterministic activation state
   */
  const updateAudienceDeterministicActivated = useCallback(
    (activatedMarket, activatedAdvertiser) => {
      common.updateAudienceDeterministicActivated(activatedMarket, activatedAdvertiser, context, {
        channel: audienceChannel,
      });
      updateAudienceActivationUi();
    },
    [context, audienceChannel, updateAudienceActivationUi]
  );

  /**
   * Update amplified activation state
   */
  const updateAudienceAmplifiedActivated = useCallback(
    (activatedMarket, activatedAdvertiser, activatedAmplifiedThreshold) => {
      common.updateAudienceAmplifiedActivated(
        activatedMarket,
        activatedAdvertiser,
        activatedAmplifiedThreshold,
        context,
        { channel: audienceChannel }
      );
      updateAudienceActivationUi();
    },
    [context, audienceChannel, updateAudienceActivationUi]
  );

  /**
   * Update always-on activation state
   */
  const updateAudienceAlwaysOnActivated = useCallback(
    (activatedDataContractId, activatedDataContractText, activatedCategoryId, activatedAlwaysOnThreshold) => {
      common.updateAudienceAlwaysOnActivated(
        activatedDataContractId,
        activatedDataContractText,
        activatedCategoryId,
        activatedAlwaysOnThreshold,
        context,
        { channel: audienceChannel }
      );
      updateAudienceActivationUi();
    },
    [context, audienceChannel, updateAudienceActivationUi]
  );

  return {
    // States
    isActivateAudienceEnabledState,
    activateAudienceDisabledText,
    isActivateAudienceVisible,
    isActivateEnabled,
    marketsAndAdvertisersPromise,

    // Audience info
    audienceId,
    audienceName,
    isAudienceDeterministicActivated,
    isAudienceAmplifiedActivated,
    isAudienceAlwaysOnActivated,
    activatedAmplifiedThreshold,
    activatedAlwaysOnThreshold,
    activatedMarket,
    activatedAdvertiser,
    activatedDataContractId,
    activatedDataContractText,
    activatedCategoryId,

    // Permissions
    hasAmplificationModeSelector,
    isDeterministicDynamicAllowed,
    allowDeterministicSegment,
    allowCustomPriceSegment,
    allowOptimizeSegment,

    // Handlers
    updateAudienceActivationUi,
    updateAudienceDeterministicActivated,
    updateAudienceAmplifiedActivated,
    updateAudienceAlwaysOnActivated,
  };
};

export default useAudienceActivation;
